/**
 * Billing > Usage (Phase 21 / Task 21.7c): every billable customer's meters for a month,
 * rolled up in the billing time zone. GET /v1/platform/billing/usage?month=YYYY-MM.
 *
 * Every meter is shown, priced or not (D3). A meter with no data reads "no count taken", one with
 * no source "not collected", each with hover help (OPEN-197), and a partly captured one says how
 * many days it has: never a zero that was not counted. "Take today's snapshot" (POST /usage/snapshot) is for the dev walk;
 * the nightly sweeper takes it otherwise, and a repeat on the same day does nothing.
 */
import * as React from 'react';
import { toast } from 'sonner';
import { Button } from 'src/components/ui/button';
import { SortButton } from 'src/components/ui/sortable-head';
import { fieldStyle, subText } from '../../ui/bits';
import { ariaSort, sortRows, useUrlSort } from '../../lib/url-state';
import {
  useBillingToday,
  useSnapshotNow,
  useUsage,
  type CustomerTenantType,
  type UsageMeter,
} from './billing-hooks';
import { billingErrorText } from './billing-format';
import { meterCell, meterHint, previousMonth } from './invoice-format';
import { METER_LABELS } from './rate-card-draft';

/** A meter's cell; one with no number, or a partial count, carries why as hover help. */
function MeterCell({ m }: { m: UsageMeter }): React.JSX.Element {
  const hint = meterHint(m);
  if (hint === null) return <>{meterCell(m)}</>;
  return (
    <span
      title={hint}
      style={{
        ...(m.value === null ? subText : {}),
        textDecoration: 'underline dotted',
        cursor: 'help',
      }}
    >
      {meterCell(m)}
    </span>
  );
}

/**
 * OPEN-204 — the usage table sorts by Customer or by any meter column (its meter code is the
 * key). The API sends customers by type (hauler, operator, disposal), then name: `type` is that
 * order, with no header of its own.
 */
const USAGE_SORTS = ['type', 'customer'] as const;
const TYPE_RANK: Readonly<Record<CustomerTenantType, number>> = {
  HAULER: 0,
  OPERATOR: 1,
  DISPOSAL: 2,
};

function UsageTable({ month }: { month: string }): React.JSX.Element {
  const { data, isLoading, isError } = useUsage(month);
  const loaded = data?.customers ?? [];
  const meters = [...new Set(loaded.flatMap((c) => c.meters.map((m) => m.meter)))];
  const { sort, toggle } = useUrlSort<string>(
    [...USAGE_SORTS, ...meters],
    { key: 'type', dir: 'asc' },
    [],
  );
  const customers = sortRows(loaded, sort, (c, k) => {
    if (k === 'type') return TYPE_RANK[c.tenantType];
    if (k === 'customer') return c.tenantName;
    // A meter: its number, or nothing (sorts last) when it has none.
    const v = c.meters.find((x) => x.meter === k)?.value ?? null;
    return v === null ? null : Number(v);
  });
  if (isLoading) return <p style={subText}>Loading…</p>;
  if (isError || data === undefined) {
    return (
      <p role="alert" style={{ color: 'var(--color-danger)' }}>
        Could not load the usage for {month}.
      </p>
    );
  }
  return (
    <div style={{ overflowX: 'auto' }}>
      <p style={subText}>
        {data.periodStart} to {data.periodEnd} ({data.timezone}), {data.daysInPeriod} days.
      </p>
      <table style={{ borderCollapse: 'collapse', fontSize: '13px', minWidth: '100%' }}>
        <thead>
          <tr style={{ textAlign: 'left', ...subText }}>
            <th style={{ padding: '6px 8px' }} aria-sort={ariaSort(sort, 'customer')}>
              <SortButton column="customer" sort={sort} onSort={toggle}>
                Customer
              </SortButton>
            </th>
            {meters.map((m) => (
              <th key={m} style={{ padding: '6px 8px' }} aria-sort={ariaSort(sort, m)}>
                <SortButton column={m} sort={sort} onSort={toggle}>
                  {METER_LABELS[m] ?? m}
                </SortButton>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {customers.map((c) => (
            <tr
              key={c.tenantId}
              data-testid={`usage-${c.tenantId}`}
              style={{ borderTop: '1px solid var(--color-border-primary)' }}
            >
              <td style={{ padding: '6px 8px' }}>{c.tenantName}</td>
              {meters.map((name) => {
                const m = c.meters.find((x) => x.meter === name);
                return (
                  <td key={name} style={{ padding: '6px 8px' }}>
                    {m === undefined ? '—' : <MeterCell m={m} />}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function UsageForMonth({ today }: { today: string }): React.JSX.Element {
  const [month, setMonth] = React.useState(previousMonth(today));
  const snapshot = useSnapshotNow({
    onSuccess: () => toast.success("Today's usage snapshot is taken."),
    onError: (err) => toast.error(billingErrorText(err, 'Could not take the snapshot.')),
  });
  return (
    <div>
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '12px' }}>
        <label style={{ ...subText, display: 'flex', gap: '6px', alignItems: 'center' }}>
          Month
          <input
            type="month"
            aria-label="Usage month"
            value={month}
            onChange={(e): void => setMonth(e.target.value)}
            style={{ ...fieldStyle, width: 'auto' }}
          />
        </label>
        <Button type="button" variant="outline" onClick={(): void => snapshot.mutate({})}>
          Take today&apos;s snapshot
        </Button>
      </div>
      {month !== '' && <UsageTable month={month} />}
    </div>
  );
}

export function UsagePage(): React.JSX.Element {
  const today = useBillingToday();
  if (today === null) return <p style={subText}>Loading…</p>;
  return <UsageForMonth today={today} />;
}
